<template>
  <div class="search-dropdown" v-if="options" :style="componentWidth">
    <!-- Dropdown Input -->
    <input class="search-input"
    autocomplete="off"
    :class="[searchInputStyle ? searchInputStyle :'',
      {'add-image':iconRequired}]"
      @click="inputClick()"
      @blur="exit()"
      @keyup="keyMonitor"
      :id="elementId"
      v-model="searchFilter"
      :readonly="!searchEnable"
      :disabled="disabled"
      :maxlength="maxlengthChar"
      :placeholder="selectedText" />
    <div v-if="!hideIcon" @click="showOptions()" class="triangle"
    :class="[triangleCustomStyle ? triangleCustomStyle :'', disabled ? 'disabled-cursor' : '', optionsShown ? 'active' : '']">
    </div>

    <!-- Dropdown Menu -->
    <div class="dropdown-content" v-bind:id="`${elementId}Content`"
    :class="{'add-padding':iconRequired}"
      v-if="optionsShown">
      <div
        class="dropdown-item"
        v-bind:id="'item' + index"
        @mouseover="mouseOver(index)"
        @mousedown="selectOption(option)"
        v-for="(option, index) in filteredOptions"
        :key="index" :ref="'item' + index"
        :class="{ focusItem: index === focus,
        selectedItem: highlightSelectedItem(index)}">
          {{ option.Name }}
      </div>
    </div>
    <span class="input-placeholder placeholder" :class="[searchFilter || selectedItem ? 'animate' : '']">{{placeholder}}</span>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';

// Define component options
defineOptions({
  name: 'SearchDropdown'
});

// Define props
const props = defineProps({
  options: {
    type: Array,
    required: false,
    default: () => [],
  },
  width: {
    type: String,
    required: false,
    default: '100%',
  },
  searchEnable: {
    type: Boolean,
    required: false,
    default: true,
  },
  placeholder: {
    type: String,
    required: false,
    default: 'Please select an option',
  },
  elementId: {
    type: String,
    required: true,
  },
  viewKey: {
    type: String,
    required: false,
    default: 'Name',
  },
  codeKey: {
    type: String,
    required: false,
    default: 'Id',
  },
  searchRegex: {
    type: String,
    required: false,
    default: null,
  },
  selectedItem: {
    type: Object,
    required: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideIcon: {
    type: Boolean,
    required: false,
    default: false,
  },
  iconRequired: {
    type: Boolean,
    required: false,
    default: false,
  },
  isFilter: {
    type: Boolean,
    required: false,
    default: false,
  },
  addCustomValue: {
    type: Boolean,
    required: false,
    default: false,
  },
  enableInputClick: {
    type: Boolean,
    required: false,
    default: true,
  },
  enableAutoSelectUpdate: {
    type: Boolean,
    required: false,
    default: false,
  },
  numberOfItemToShow: {
    type: Number,
    required: false,
    default: null,
  },
  resetDropdown: {
    type: Boolean,
    required: false,
    default: false,
  },
  maxlengthChar: {
    type: Number,
    required: false,
  },
  disableScrollToView: {
    type: Boolean,
    required: false,
    default: false,
  },
  enableCenteredSelection: {
    type: Boolean,
    required: false,
    default: false,
  },
  searchInputStyle: {
    type: String,
    required: false,
  },
  triangleCustomStyle: {
    type: String,
    required: false,
  },
});

// Define emits
const emit = defineEmits(['selectedItem', 'addSelectedItem', 'manualUserInput', 'dropdownReset']);
// Reactive data
const selected = ref(props.selectedItem);
const optionsShown = ref(false);
const searchFilter = ref('');
const selectedText = ref('');
const focus = ref(0);
const selectedIndex = ref(null);

// Computed properties
const filteredOptions = computed(() => {
  const filtered = [];
  let regexOption = null;
  if (props.searchRegex) {
    regexOption = props.searchRegex + searchFilter.value.trim();
  } else {
    regexOption = searchFilter.value.trim();
  }
  const filterValues = {
    regOption: new RegExp(regexOption, 'ig'),
    searchFilter: searchFilter.value.trim(),
    viewKey: props.viewKey,
    codeKey: props.codeKey,
  };
  if (props.options.length > 0) {
    if (!props.enableInputClick) {
      if (!searchFilter.value.trim()) {
        return filtered;
      }
    }
    const showItemCount = props.numberOfItemToShow;
    props.options.forEach((option) => {
      const Value = formatOptions(option);
      if (!filterValues.searchFilter
        || Value.Name.match(filterValues.regOption)) {
        if (showItemCount) {
          if (filtered.length < showItemCount) {
            filtered.push(Value);
          }
        } else {
          filtered.push(Value);
        }
      }
    });
  }
  return filtered;
});

const componentWidth = computed(() => {
  return {
    width: props.width,
  };
});
// Methods
const highlightSelectedItem = (index) => {
  if (props.enableCenteredSelection && selected.value) {
    return filteredOptions.value[index].Id === selected.value.Id;
  }
  return false;
};

const selectOption = (option) => {
  if (option) {
    selected.value = option;
    optionsShown.value = false;
    searchFilter.value = selected.value.Name;
    selectedText.value = selected.value.Name;
    emit('selectedItem', selected.value, props.elementId);
  }
};

const updateSearchFilter = (option) => {
  if (option) {
    optionsShown.value = false;
    searchFilter.value = option.Name;
    selectedText.value = option.Name;
  }
};

const inputClick = () => {
  if (props.enableInputClick) {
    showOptions();
  }
  if (props.enableAutoSelectUpdate) {
    emit('manualUserInput', true);
  }
};

const showOptions = () => {
  if (!props.disabled) {
    optionsShown.value = !optionsShown.value;
    if (optionsShown.value) {
      searchFilter.value = '';
      nextTick(() => {
        const dropdownContent = document.getElementById(`${props.elementId}Content`);
        const dropdownItem = document.getElementById('item0');
        const computedDropdownItem = window.getComputedStyle(dropdownItem);
        const dropdownContentHeight = (parseInt(computedDropdownItem.height, 10)
          + parseInt(computedDropdownItem.paddingTop, 10)
          + parseInt(computedDropdownItem.paddingBottom, 10)
          + parseInt(computedDropdownItem.marginTop, 10)
          + parseInt(computedDropdownItem.marginBottom, 10)) * 7;
        dropdownContent.style.maxHeight = props.enableCenteredSelection
          ? `${dropdownContentHeight}px`
          : '130px';
        if (props.selectedItem) {
          scrollToSelectedItem();
        } else {
          focus.value = 0;
        }
        scrollIntoView();
      });
    }
  }
};

const scrollToSelectedItem = () => {
  if (!selected.value) {
    return;
  }
  focus.value = filteredOptions.value.findIndex(item => item.Name === selected.value.Name);
  selectedIndex.value = focus.value;
  if (optionsShown.value && focus.value >= 0) {
    const str = 'item';
    const refItem = str + focus.value;
    const myElement = document.getElementById(refItem);
    const wrapper = document.getElementById(`${props.elementId}Content`);
    const computedElement = window.getComputedStyle(myElement);
    const offsetToCenter = (parseInt(computedElement.height, 10)
      + parseInt(computedElement.paddingTop, 10)
      + parseInt(computedElement.paddingBottom, 10)
      + parseInt(computedElement.marginTop, 10)
      + parseInt(computedElement.marginBottom, 10)) * 3;
    const count = props.enableCenteredSelection
      ? myElement.offsetTop - wrapper.scrollTop - offsetToCenter
      : myElement.offsetTop - wrapper.scrollTop;

    wrapper.scrollBy({ top: count, left: 0 });
  } else {
    scrollIntoView();
  }
};

const scrollIntoView = () => {
  if (!props.disableScrollToView) {
    const myElement = document.getElementById(props.elementId);
    myElement.scrollIntoView();
  }
};
const exit = () => {
  if (selected.value) {
    if (!selected.value.Id) {
      selected.value = {};
      if (searchFilter.value.trim() && props.addCustomValue) {
        selected.value = {
          Name: searchFilter.value,
          Id: 0,
        };
        selectOption(selected.value);
        emit('addSelectedItem');
      } else {
        searchFilter.value = '';
      }
    } else if (searchFilter.value.trim() && props.addCustomValue
      && searchFilter.value.trim() !== selected.value.Name) {
      selected.value = {
        Name: searchFilter.value,
        Id: 0,
      };
      selectOption(selected.value);
    } else {
      searchFilter.value = selected.value.Name;
    }
  } else if (!selected.value && searchFilter.value.trim() && props.addCustomValue) {
    selected.value = {
      Name: searchFilter.value,
      Id: 0,
    };
    selectOption(selected.value);
  } else if(!selected.value && searchFilter.value.trim() && !props.addCustomValue) {
    searchFilter.value = "";
  }
  setTimeout(() => {
      optionsShown.value = false;
      focus.value = 0;
  }, 200);
};

const addCustomValueOnEnter = () => {
  const customValue = {};
  customValue[props.viewKey] = searchFilter.value.trim();
  selectOption(customValue);
  emit('addSelectedItem');
};

// Selecting when pressing Enter
const keyMonitor = (event) => {
  if (event.key === 'Enter' && filteredOptions.value[0]) {
    if (optionsShown.value) {
      if (focus.value !== null) {
        selectOption(filteredOptions.value[focus.value]);
        emit('addSelectedItem');
      } else if (focus.value === null && props.addCustomValue && searchFilter.value.trim()) {
        addCustomValueOnEnter();
      }
    } else if (!optionsShown.value && selected.value) {
      emit('addSelectedItem');
    }
  } else if (event.keyCode === 13 && props.addCustomValue
  && !filteredOptions.value[0] && searchFilter.value.trim()) {
    addCustomValueOnEnter();
  } else if (event.keyCode === 38 && filteredOptions.value.length > 0) {
    if (focus.value !== null && focus.value > 0) {
      focus.value = focus.value - 1;
    }
  } else if (event.keyCode === 40 && filteredOptions.value.length > 0) {
    if (focus.value === null) {
      focus.value = 0;
    } else if (focus.value < filteredOptions.value.length - 1) {
      focus.value = focus.value + 1;
    }
  }
  // searchFilter.value = searchFilter.value.replace(/[^\w\s]/gi, '');
  if (searchFilter.value.trim() || props.enableInputClick) {
    optionsShown.value = true;
  } else {
    optionsShown.value = false;
  }
  if (filteredOptions.value.length === 0) {
    optionsShown.value = false;
  }
  if (optionsShown.value && focus.value) {
    const str = 'item';
    const refItem = str + focus.value;
    const myElement = document.getElementById(refItem);
    const wrapper = document.getElementById(`${props.elementId}Content`);
    const count = myElement.offsetTop - wrapper.scrollTop - 90;
    wrapper.scrollBy({ top: count, left: 0 });
  }
};

const formatOptions = (obj) => {
  const viewValue = props.viewKey.split('.').reduce((a, b) => a[b], obj);
  const codeValue = props.codeKey.split('.').reduce((a, b) => a[b], obj);
  return {
    Name: viewValue,
    Id: codeValue,
  };
};

const mouseOver = (index) => {
  focus.value = index;
};
// Watchers
watch(() => props.selectedItem, (newItem, oldItem) => {
  if (newItem) {
    if (newItem.Name === props.placeholder) {
      // to handle case when dropdown is reset (om event case).
      selected.value = null;
      optionsShown.value = false;
      searchFilter.value = null;
      selectedText.value = props.placeholder;
    } else if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
      selectOption(formatOptions(newItem));
    } else {
      updateSearchFilter(formatOptions(newItem));
    }
  }
});

watch(searchFilter, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    focus.value = null;
  }
});

watch(() => props.resetDropdown, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    if (newVal) {
      selected.value = null;
      searchFilter.value = '';
      selectedText.value = props.placeholder;
      optionsShown.value = false;
      emit('dropdownReset');
    }
  }
});

// Lifecycle hooks
onMounted(() => {
  selectOption(selected.value);
});
</script>

<style lang="stylus" scoped>

.input-placeholder {
  padding: 15px 0 15px 10px;
  pointer-events: none;
  position:absolute;
  left:0;
  top:0;
  transition: 0.3s;
  transition-timing-function: ease;
  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 1);
  opacity:0.5;
  color: #868894;
}
.search-dropdown {
  display: inline-block;
  position: relative;
  font-size: 14px;
  box-sizing: border-box;
  font-family: 'Lato',sans-serif;
  input:disabled {
      cursor: no-drop;
      opacity: 0.4;
  }
  .placeholder {
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 13px 0 12px 10px !important;
      color: #666666;
      transform: none;
      font-size: 14px;
      opacity: 0.5;
  }
  .animate {
      transform: translateY(-6.5px) translateX(10px);
      font-size:11px;
      padding: 0px 2px 0px 2px !important;
      background-color: #fff;
      width: auto;
      opacity: 1;
  }

  .search-input {
      width: 100%;
      cursor: pointer;
      border: 1px solid #CBD3DA;
      border-radius: 4px;
      padding: 0 30px 0 10px;
      margin-top: 0;
      height: 42px;
      color: #3a4145;
      box-sizing: border-box;
      font-family: 'Lato',sans-serif;
      font-size: 14px;
      text-overflow: ellipsis;
      &:focus {
        border-color: #2e90fa;
        outline: none;
        ~ .animate {
          color: #2e90fa;
        }
      }
      ::-webkit-input-placeholder { /* Edge */
        color: #666;
        opacity: 0.5;
      }
      ::placeholder {
        color: #666;
        opacity: 0.5;
      }
      ::-ms-clear {
      display: none;
      }
  }

  .triangle {
    position: absolute;
    width: 40px;
    height: 40px;
    right: 1px;
    top: 1px;
    padding: 4px 8px;
    text-align: center;
    transition: transform .2s ease;
    box-sizing: border-box;
    &:before {
      position: relative;
      right: 0;
      top: 65%;
      margin-top: 4px;
      border-color: #3a4145 transparent transparent;
      border-style: solid;
      border-width: 5px 5px 0;
      content: "";
    }
    &.active {
      transform: rotate(180deg);
    }
  }
  .disabled-cursor {
    cursor: no-drop;
  }
  .add-padding{
    padding-left: 23px;
  }
  .dropdown-content {
      width: 100%;
      overflow: auto;
      z-index: 999;
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      position: absolute;
      box-sizing: border-box;
      font-size: 14px;
      font-family: 'Lato', sans-serif;
      color: #3a4145;
      background: #fafbfc;
      border: 1px solid #dee2e6;
      max-height: 210px;
      text-align: left;
      .dropdown-item {
        text-decoration: none;
        cursor: pointer;
        padding: 0px 12px 0px 12px;
        min-height: 12px;
        line-height: 30px;
        vertical-align: middle;
      }
      .focusItem {
          background: #0facf3;
      }
      .selectedItem {
        font-weight: 700;
      }
    }
}
</style>
